import { useCallback, useMemo } from 'react';

import { useHouseholdWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts/useHouseholdWorkflowTrackersContext';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import type { WorkflowTracker } from '@/types/schema';
import { WorkflowTrackerKind, WorkflowTrackerStatus } from '@/types/schema';

interface UseEstateSuggestionGenerationStateOptions {
  entityId: string | null | undefined;
  workflowTrackerKind: WorkflowTrackerKind;
  onCompleted?: () => void | Promise<void>;
}

function doesTrackerMatchEntityId(
  tracker: WorkflowTracker,
  entityId: string | null | undefined
): boolean {
  if (!entityId) return false;

  return (
    tracker.entities?.some((trackedEntity) => trackedEntity.id === entityId) ||
    tracker.testamentaryEntities?.some(
      (trackedEntity) => trackedEntity.id === entityId
    ) ||
    false
  );
}

export function useEstateSuggestionGenerationState({
  entityId,
  workflowTrackerKind,
  onCompleted,
}: UseEstateSuggestionGenerationStateOptions) {
  const { trackers } = useHouseholdWorkflowTrackersContext();
  const doesTrackerMatch = useCallback(
    (tracker: WorkflowTracker) => doesTrackerMatchEntityId(tracker, entityId),
    [entityId]
  );

  const matchingTrackers = useMemo(
    () =>
      trackers.filter(
        (tracker) =>
          tracker.kind === workflowTrackerKind && doesTrackerMatch(tracker)
      ),
    [doesTrackerMatch, trackers, workflowTrackerKind]
  );
  const inProgressTrackerIDs = useMemo(
    () =>
      new Set(
        matchingTrackers
          .filter(
            (tracker) =>
              tracker.status === WorkflowTrackerStatus.Queued ||
              tracker.status === WorkflowTrackerStatus.Started
          )
          .map((tracker) => tracker.id)
      ),
    [matchingTrackers]
  );

  useOnHouseholdWorkflowTrackerComplete({
    kind: workflowTrackerKind,
    filter: doesTrackerMatch,
    onCompleted: async () => {
      await onCompleted?.();
    },
  });

  return inProgressTrackerIDs.size > 0;
}
